// TMS Design System - Chakra UI v3 Theme Configuration
// This file integrates TMS design tokens with Chakra UI v3's theme system

import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

// import { colors } from '../tokens/colors';
import { typography } from '../tokens/typography';
import { spacing, borderRadius, zIndex } from '../tokens/spacing';
import { shadows } from '../tokens/shadows';
// import { breakpoints } from '../tokens';
import { animations } from '../tokens';

// Convert TMS color tokens to Chakra UI v3 format
const createColorTokens = () => {
  const colorTokens: Record<string, Record<string, { value: string }>> = {};

  // Standard color palettes (required for Badge colorPalette support)
  // Gray palette
  colorTokens.gray = {
    50: { value: '#F9FAFB' },
    100: { value: '#F3F4F6' },
    200: { value: '#E5E7EB' },
    300: { value: '#D1D5DB' },
    400: { value: '#9CA3AF' },
    500: { value: '#6B7280' },
    600: { value: '#4B5563' },
    700: { value: '#374151' },
    800: { value: '#1F2937' },
    900: { value: '#111827' },
    950: { value: '#030712' },
  };

  // Red palette
  colorTokens.red = {
    50: { value: '#FEF2F2' },
    100: { value: '#FEE2E2' },
    200: { value: '#FECACA' },
    300: { value: '#FCA5A5' },
    400: { value: '#F87171' },
    500: { value: '#EF4444' },
    600: { value: '#DC2626' },
    700: { value: '#B91C1C' },
    800: { value: '#991B1B' },
    900: { value: '#7F1D1D' },
    950: { value: '#450A0A' },
  };

  // Orange palette
  colorTokens.orange = {
    50: { value: '#FFF7ED' },
    100: { value: '#FFEDD5' },
    200: { value: '#FED7AA' },
    300: { value: '#FDBA74' },
    400: { value: '#FB923C' },
    500: { value: '#F97316' },
    600: { value: '#EA580C' },
    700: { value: '#C2410C' },
    800: { value: '#9A3412' },
    900: { value: '#7C2D12' },
    950: { value: '#431407' },
  };

  // Yellow palette
  colorTokens.yellow = {
    50: { value: '#FEFCE8' },
    100: { value: '#FEF9C3' },
    200: { value: '#FEF08A' },
    300: { value: '#FDE047' },
    400: { value: '#FACC15' },
    500: { value: '#EAB308' },
    600: { value: '#CA8A04' },
    700: { value: '#A16207' },
    800: { value: '#854D0E' },
    900: { value: '#713F12' },
    950: { value: '#422006' },
  };

  // Green palette
  colorTokens.green = {
    50: { value: '#F0FDF4' },
    100: { value: '#DCFCE7' },
    200: { value: '#BBF7D0' },
    300: { value: '#86EFAC' },
    400: { value: '#4ADE80' },
    500: { value: '#22C55E' },
    600: { value: '#16A34A' },
    700: { value: '#15803D' },
    800: { value: '#166534' },
    900: { value: '#14532D' },
    950: { value: '#052E16' },
  };

  // Blue palette
  colorTokens.blue = {
    50: { value: '#EFF6FF' },
    100: { value: '#DBEAFE' },
    200: { value: '#BFDBFE' },
    300: { value: '#93C5FD' },
    400: { value: '#60A5FA' },
    500: { value: '#3B82F6' },
    600: { value: '#2563EB' },
    700: { value: '#1D4ED8' },
    800: { value: '#1E40AF' },
    900: { value: '#1E3A8A' },
    950: { value: '#172554' },
  };

  // Purple palette
  colorTokens.purple = {
    50: { value: '#FAF5FF' },
    100: { value: '#F3E8FF' },
    200: { value: '#E9D5FF' },
    300: { value: '#D8B4FE' },
    400: { value: '#C084FC' },
    500: { value: '#A855F7' },
    600: { value: '#9333EA' },
    700: { value: '#7E22CE' },
    800: { value: '#6B21A8' },
    900: { value: '#581C87' },
    950: { value: '#3B0764' },
  };

  // Brand colors with scale
  colorTokens.brand = {
    50: { value: '#E6F0FF' },
    100: { value: '#CCE0FF' },
    200: { value: '#99C2FF' },
    300: { value: '#66A3FF' },
    400: { value: '#3385FF' },
    500: { value: '#0066CC' }, // colors.brand.primary
    600: { value: '#0052A3' },
    700: { value: '#003D7A' },
    800: { value: '#002147' }, // colors.brand.dark
    900: { value: '#001429' },
    950: { value: '#000A14' },
  };

  // Semantic colors
  colorTokens.success = {
    50: { value: '#F0FDF4' },
    100: { value: '#DCFCE7' },
    200: { value: '#BBF7D0' },
    300: { value: '#86EFAC' },
    400: { value: '#4ADE80' },
    500: { value: '#28A745' }, // colors.semantic.success
    600: { value: '#16A34A' },
    700: { value: '#15803D' },
    800: { value: '#166534' },
    900: { value: '#14532D' },
    950: { value: '#052E16' },
  };

  colorTokens.warning = {
    50: { value: '#FFFBEB' },
    100: { value: '#FEF3C7' },
    200: { value: '#FDE68A' },
    300: { value: '#FCD34D' },
    400: { value: '#FBBF24' },
    500: { value: '#FFC107' }, // colors.semantic.warning
    600: { value: '#D97706' },
    700: { value: '#B45309' },
    800: { value: '#92400E' },
    900: { value: '#78350F' },
    950: { value: '#451A03' },
  };

  colorTokens.danger = {
    50: { value: '#FEF2F2' },
    100: { value: '#FEE2E2' },
    200: { value: '#FECACA' },
    300: { value: '#FCA5A5' },
    400: { value: '#F87171' },
    500: { value: '#DC3545' }, // colors.semantic.danger
    600: { value: '#DC2626' },
    700: { value: '#B91C1C' },
    800: { value: '#991B1B' },
    900: { value: '#7F1D1D' },
    950: { value: '#450A0A' },
  };

  colorTokens.info = {
    50: { value: '#F0F9FF' },
    100: { value: '#E0F2FE' },
    200: { value: '#BAE6FD' },
    300: { value: '#7DD3FC' },
    400: { value: '#38BDF8' },
    500: { value: '#17A2B8' }, // colors.semantic.info
    600: { value: '#0284C7' },
    700: { value: '#0369A1' },
    800: { value: '#075985' },
    900: { value: '#0C4A6E' },
    950: { value: '#082F49' },
  };

  // Logistics-specific colors
  colorTokens.logistics = {
    economy: { value: '#9CA3AF' },
    standard: { value: '#3B82F6' },
    express: { value: '#F59E0B' },
    cargo: { value: '#8B5CF6' },
  };

  return colorTokens;
};

// Convert TMS typography tokens to Chakra UI v3 format
const createTypographyTokens = () => {
  return {
    fonts: {
      heading: { value: typography.fontFamily.sans },
      body: { value: typography.fontFamily.sans },
      mono: { value: typography.fontFamily.mono },
    },
    fontSizes: {
      xs: { value: typography.fontSize['body-xs'] },
      sm: { value: typography.fontSize['body-sm'] },
      md: { value: typography.fontSize.body },
      lg: { value: typography.fontSize['body-lg'] },
      xl: { value: typography.fontSize.h6 },
      '2xl': { value: typography.fontSize.h5 },
      '3xl': { value: typography.fontSize.h4 },
      '4xl': { value: typography.fontSize.h3 },
      '5xl': { value: typography.fontSize.h2 },
      '6xl': { value: typography.fontSize.h1 },
      '7xl': { value: typography.fontSize['display-2'] },
      '8xl': { value: typography.fontSize['display-1'] },
    },
    fontWeights: {
      thin: { value: '100' },
      light: { value: '300' },
      normal: { value: typography.fontWeight.normal },
      medium: { value: typography.fontWeight.medium },
      semibold: { value: typography.fontWeight.semibold },
      bold: { value: typography.fontWeight.bold },
      extrabold: { value: '800' },
      black: { value: '900' },
    },
    lineHeights: {
      normal: { value: typography.lineHeight.base },
      none: { value: '1' },
      shorter: { value: '1.25' },
      short: { value: '1.375' },
      base: { value: typography.lineHeight.base },
      tall: { value: '1.625' },
      taller: { value: '2' },
    },
  };
};

// Convert spacing tokens
const createSpacingTokens = () => {
  const spacingTokens: Record<string, { value: string }> = {};

  Object.entries(spacing).forEach(([key, value]) => {
    // Only process string values, skip nested objects
    if (typeof value === 'string') {
      spacingTokens[key] = { value };
    }
  });

  return spacingTokens;
};

// Create semantic tokens for consistent theming
const createSemanticTokens = () => {
  return {
    colors: {
      // Background colors
      'bg.canvas': {
        value: { base: '#F8F9FA', _dark: '#212529' }, // colors.background.secondary, colors.gray[900]
      },
      'bg.default': {
        value: { base: '#FFFFFF', _dark: '#343A40' }, // colors.background.primary, colors.gray[800]
      },
      'bg.subtle': {
        value: { base: '#F8F9FA', _dark: '#495057' }, // colors.gray[100], colors.gray[700]
      },
      // Elevated surfaces (cards, popovers, menus). Chakra-default token names that
      // were referenced app-wide but never defined here → previously rendered
      // transparent (visible on floating popovers/hints). Defined opaque + dark-aware.
      'bg.panel': {
        value: { base: '#FFFFFF', _dark: '#343A40' }, // elevated surface = bg.default
      },
      'bg.surface': {
        value: { base: '#FFFFFF', _dark: '#343A40' }, // surface alias = bg.default
      },
      'bg.muted': {
        value: { base: '#F1F3F5', _dark: '#3A4046' }, // muted fill (gray.100/gray.750)
      },
      'bg.emphasized': {
        value: { base: '#E9ECEF', _dark: '#41464B' }, // emphasized fill (gray.200/gray.650)
      },
      'bg.error': {
        value: { base: '#FDECEA', _dark: '#4A2420' }, // error surface tint
      },

      // Text colors
      'fg.default': {
        value: { base: '#212529', _dark: '#FFFFFF' }, // colors.text.primary, colors.text.inverse
      },
      'fg.muted': {
        value: { base: '#6C757D', _dark: '#CED4DA' }, // colors.text.secondary, colors.gray[400]
      },
      'fg.subtle': {
        value: { base: '#ADB5BD', _dark: '#ADB5BD' }, // colors.text.tertiary, colors.gray[500]
      },

      // Border colors
      'border.default': {
        value: { base: '#CED4DA', _dark: '#6C757D' }, // colors.border.default, colors.gray[600]
      },
      'border.muted': {
        value: { base: '#DEE2E6', _dark: '#495057' }, // colors.border.light, colors.gray[700]
      },
      'border.subtle': {
        value: { base: '#E9ECEF', _dark: '#495057' }, // colors.gray[200], colors.gray[700]
      },
      // Referenced app-wide but previously undefined → fell back to transparent borders.
      'border.emphasized': {
        value: { base: '#ADB5BD', _dark: '#868E96' }, // stronger than border.default
      },
      'border.error': {
        value: { base: '#F5C2C7', _dark: '#842029' }, // error border
      },

      // Focus colors
      'focus.default': {
        value: { base: '#0066CC', _dark: '#4285F4' }, // colors.brand.primary, colors.brand.secondary
      },

      // =================================================================
      // Color Palette Semantic Tokens (required for Badge, Button, etc.)
      // These enable colorPalette prop to work with variant="subtle" etc.
      // =================================================================

      // Gray semantic tokens
      gray: {
        solid: { value: '{colors.gray.600}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.gray.700}' },
        muted: { value: '{colors.gray.100}' },
        subtle: { value: '{colors.gray.100}' },
        emphasized: { value: '{colors.gray.200}' },
        focusRing: { value: '{colors.gray.500}' },
      },

      // Red semantic tokens
      red: {
        solid: { value: '{colors.red.600}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.red.800}' },
        muted: { value: '{colors.red.100}' },
        subtle: { value: '{colors.red.100}' },
        emphasized: { value: '{colors.red.200}' },
        focusRing: { value: '{colors.red.500}' },
      },

      // Orange semantic tokens
      orange: {
        solid: { value: '{colors.orange.500}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.orange.800}' },
        muted: { value: '{colors.orange.100}' },
        subtle: { value: '{colors.orange.100}' },
        emphasized: { value: '{colors.orange.200}' },
        focusRing: { value: '{colors.orange.500}' },
      },

      // Yellow semantic tokens
      yellow: {
        solid: { value: '{colors.yellow.500}' },
        contrast: { value: '{colors.yellow.900}' },
        fg: { value: '{colors.yellow.800}' },
        muted: { value: '{colors.yellow.100}' },
        subtle: { value: '{colors.yellow.100}' },
        emphasized: { value: '{colors.yellow.200}' },
        focusRing: { value: '{colors.yellow.500}' },
      },

      // Green semantic tokens
      green: {
        solid: { value: '{colors.green.600}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.green.800}' },
        muted: { value: '{colors.green.100}' },
        subtle: { value: '{colors.green.100}' },
        emphasized: { value: '{colors.green.200}' },
        focusRing: { value: '{colors.green.500}' },
      },

      // Blue semantic tokens
      blue: {
        solid: { value: '{colors.blue.600}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.blue.800}' },
        muted: { value: '{colors.blue.100}' },
        subtle: { value: '{colors.blue.100}' },
        emphasized: { value: '{colors.blue.200}' },
        focusRing: { value: '{colors.blue.500}' },
      },

      // Purple semantic tokens
      purple: {
        solid: { value: '{colors.purple.600}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.purple.800}' },
        muted: { value: '{colors.purple.100}' },
        subtle: { value: '{colors.purple.100}' },
        emphasized: { value: '{colors.purple.200}' },
        focusRing: { value: '{colors.purple.500}' },
      },

      // Brand semantic tokens
      brand: {
        solid: { value: '{colors.brand.500}' },
        contrast: { value: '#FFFFFF' },
        fg: { value: '{colors.brand.700}' },
        muted: { value: '{colors.brand.100}' },
        subtle: { value: '{colors.brand.50}' },
        emphasized: { value: '{colors.brand.200}' },
        focusRing: { value: '{colors.brand.500}' },
      },
    },

    // Radii tokens
    radii: {
      l1: { value: borderRadius.sm },
      l2: { value: borderRadius.md },
      l3: { value: borderRadius.lg },
      full: { value: borderRadius.full },
    },

    // Shadow tokens
    shadows: {
      sm: { value: shadows.sm },
      md: { value: shadows.md },
      lg: { value: shadows.lg },
      xl: { value: shadows.xl },
      '2xl': { value: shadows['2xl'] },
    },
  };
};

// Create animation tokens
const createAnimationTokens = () => {
  return {
    durations: {
      fastest: { value: animations.duration.instant },
      faster: { value: animations.duration.fast },
      fast: { value: animations.duration.normal },
      normal: { value: animations.duration.normal },
      slow: { value: animations.duration.slow },
      slower: { value: animations.duration.slower },
      slowest: { value: animations.duration.slowest },
    },
    easings: {
      'ease-in': { value: animations.easing.easeIn },
      'ease-out': { value: animations.easing.easeOut },
      'ease-in-out': { value: animations.easing.easeInOut },
    },
  };
};

// Create the custom theme configuration
export const tmsThemeConfig = defineConfig({
  // SSR-safe configuration
  cssVarsRoot: ':where(:root, :host)',
  preflight: true,
  theme: {
    breakpoints: {
      base: '0em',
      sm: '40em', // 640px
      md: '48em', // 768px
      lg: '64em', // 1024px
      xl: '80em', // 1280px
      '2xl': '96em', // 1536px
    },
    tokens: {
      colors: createColorTokens(),
      ...createTypographyTokens(),
      spacing: createSpacingTokens(),
      radii: {
        none: { value: borderRadius.none },
        xs: { value: '0.125rem' }, // 2px
        sm: { value: borderRadius.sm },
        md: { value: borderRadius.md },
        lg: { value: borderRadius.lg },
        xl: { value: borderRadius.xl },
        '2xl': { value: borderRadius['2xl'] },
        '3xl': { value: '1.5rem' }, // 24px
        full: { value: borderRadius.full },
      },
      animations: createAnimationTokens(),
      zIndex: {
        hide: { value: String(zIndex.hide) },
        base: { value: String(zIndex.base) },
        dropdown: { value: String(zIndex.dropdown) },
        sticky: { value: String(zIndex.sticky) },
        overlay: { value: String(zIndex.overlay) },
        modal: { value: String(zIndex.modal) },
        popover: { value: String(zIndex.popover) },
        toast: { value: String(zIndex.toast) },
        tooltip: { value: String(zIndex.tooltip) },
      },
    },
    semanticTokens: createSemanticTokens(),
  },
  // Global CSS removed to prevent hydration mismatch (empty object required by Chakra)
  // Original html/body styles moved to globals.css for SSR compatibility
  globalCss: {
    // Table UX: wider cell padding + subtle vertical dividers (platform-wide)
    'th, td': {
      borderRight: '1px solid var(--chakra-colors-border-muted)',
      paddingInline: '14px !important',
      paddingBlock: '10px !important',
    },
    'th:last-child, td:last-child': {
      borderRight: 'none',
    },
  },
  // Conditions for responsive and state-based styles
  conditions: {
    light: '[data-theme=light] &',
    dark: '[data-theme=dark] &',
    hover: '&:is(:hover, [data-hover])',
    active: '&:is(:active, [data-active])',
    focus: '&:is(:focus, [data-focus])',
    disabled: '&:is(:disabled, [disabled], [data-disabled])',
    invalid: '&:is(:invalid, [data-invalid])',
  },
});

// Create the TMS theme system with defaultConfig and our custom config
export const tmsSystem = createSystem(defaultConfig, tmsThemeConfig);

// Export theme utilities
export { defineConfig, createSystem, defaultConfig };

// Type exports for better TypeScript support
export type TMSThemeConfig = typeof tmsThemeConfig;
export type TMSSystem = typeof tmsSystem;
